/**
 * Calls for remote device sessions (ENG-2470 Phase 2), through `createApi`
 * like the rest of the app, with the session-refresh wrapper rather than the
 * generic error handler: every "no" admin gives is explained in words by the
 * dialog (`refusalText`).
 */
import { AxiosError } from "axios";
import { createApi } from "../../../utils/createApi";
import { withSessionRecovery } from "../../Troubleshooting/TroubleshootingUtils/authRecovery";
import { retryAtFrom } from "../SiteConnectivity/siteConnectivityApi";
import { formatClock } from "../SiteConnectivity/connectivityModel";
import {
  DeviceSession,
  DeviceSessionAction,
  DeviceSessionBrowserIdentity,
  DeviceSessionOutcome,
  DeviceSessionStarted,
  DeviceSessionTarget,
  DeviceSessionToken,
} from "./deviceSessionTypes";

export const DEVICE_SESSION_PATH = "device-session/";
export const deviceSessionPath = (id: string, suffix = "") =>
  `device-session/${encodeURIComponent(id)}/${suffix}`;

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" ? (value as Record<string, unknown>) : {};

const text = (value: unknown): string | null =>
  typeof value === "string" && value ? value : null;

export const NOT_PERMITTED_TEXT =
  "You don't have permission to open devices at this site. Ask your administrator if you need access.";

export const deviceSessionOutcomeFromError = <T>(
  error: unknown
): DeviceSessionOutcome<T> => {
  const axiosError = error as AxiosError;
  const status = axiosError?.response?.status;
  const body = asRecord(axiosError?.response?.data);
  const detail = text(body.detail);
  const code = text(body.code);
  const sessionId = text(body.session_id);
  const refused = (
    fallback: string,
    retryAt: string | null = null
  ): DeviceSessionOutcome<T> => ({
    kind: "refused",
    code: code ?? fallback,
    detail,
    retryAt,
    ...(sessionId ? { sessionId } : {}),
  });
  if (status === 404) {
    // admin's 404 with a code is a refusal it explains (site not found).
    return code ? refused("target_not_found") : { kind: "not_found" };
  }
  // A 502/503 with a code other than `unavailable` is a refusal admin
  // explains (not_enabled_for_site, start_failed, agent_unreachable).
  if ((status === 502 || status === 503) && code && code !== "unavailable") {
    return refused(code);
  }
  if (status === 405 || status === 501 || status === 503) {
    return { kind: "unavailable", detail };
  }
  // admin explains these 403s with a code: they are refusals, not "no access".
  if (status === 403 && code && code !== "not_permitted") {
    return refused(code);
  }
  if (status === 400 || status === 409 || status === 429) {
    return refused(
      status === 409
        ? "session_active"
        : status === 429
        ? "rate_limited"
        : "rejected",
      status === 429 ? retryAtFrom(axiosError) : null
    );
  }
  if (status === 401 || status === 403) {
    return { kind: "forbidden", code, message: NOT_PERMITTED_TEXT };
  }
  if (status != null && status >= 500) {
    return {
      kind: "error",
      message: `The server couldn't answer (error ${status}). Try again in a minute.`,
    };
  }
  return {
    kind: "error",
    message:
      "We couldn't reach the server. Check your internet connection and try again.",
  };
};

const run = async <T>(
  request: () => Promise<{ data: T }>
): Promise<DeviceSessionOutcome<T>> => {
  try {
    const response = await withSessionRecovery(request);
    return { kind: "ok", data: response.data };
  } catch (error) {
    return deviceSessionOutcomeFromError<T>(error);
  }
};

/** Start a session on one device. → `{session_id, status: "starting"}`. */
export const startDeviceSession = (
  target: Pick<DeviceSessionTarget, "siteId" | "source" | "ref" | "scheme">
): Promise<DeviceSessionOutcome<DeviceSessionStarted>> =>
  run(() =>
    createApi("").post<DeviceSessionStarted>(DEVICE_SESSION_PATH, {
      site_id: target.siteId,
      source: target.source,
      ref: target.ref,
      ...(target.scheme ? { scheme: target.scheme } : {}),
    })
  );

/** The new session's id: admin sends `session_id` (and maybe an `id` alias). */
export const startedSessionId = (
  started: DeviceSessionStarted
): string | null => text(started?.session_id) ?? text(started?.id);

export const fetchDeviceSession = (
  id: string
): Promise<DeviceSessionOutcome<DeviceSession>> =>
  run(() => createApi("").get<DeviceSession>(deviceSessionPath(id)));

/** A fresh single-use token for (re)connecting. Creator only. */
export const fetchDeviceSessionToken = (
  id: string
): Promise<DeviceSessionOutcome<DeviceSessionToken>> =>
  run(() =>
    createApi("").post<DeviceSessionToken>(deviceSessionPath(id, "token/"), {})
  );

/** `port` picks one of the session's allowed ports; admin picks the scheme. */
export const sendDeviceSessionAction = (
  id: string,
  action: DeviceSessionAction,
  port?: number,
  scheme?: "http" | "https"
): Promise<DeviceSessionOutcome<unknown>> =>
  run(() =>
    createApi("").post<unknown>(deviceSessionPath(id, "action/"), {
      action,
      ...(port != null ? { port } : {}),
      ...(scheme ? { scheme } : {}),
    })
  );

/** "Use HTTPS" starts on for the usual secure ports or a known https port. */
export const defaultUseHttps = (
  port?: number | null,
  scheme?: string | null
): boolean => scheme === "https" || port === 443 || port === 8443;

/** An action the running session can't do (admin 409 `not_supported`). */
export const ACTION_NOT_SUPPORTED_TEXT =
  "This session can't do that. End it and start a new one.";

/** A toolbar action's failure, in words. */
export const actionRefusalText = (
  outcome: Exclude<DeviceSessionOutcome<unknown>, { kind: "ok" }>
): string | null =>
  outcome.kind === "refused" && outcome.code === "not_supported"
    ? outcome.detail ?? ACTION_NOT_SUPPORTED_TEXT
    : null;

/**
 * Relaunch the remote browser as Chrome or Edge (ENG-2507). The browser
 * restarts on the current page, so this can take several seconds.
 */
export const setDeviceSessionBrowserIdentity = (
  id: string,
  identity: DeviceSessionBrowserIdentity
): Promise<DeviceSessionOutcome<unknown>> =>
  run(() =>
    createApi("").post<unknown>(deviceSessionPath(id, "action/"), {
      action: "browser_identity",
      identity,
    })
  );

export const endDeviceSession = (
  id: string
): Promise<DeviceSessionOutcome<unknown>> =>
  run(() => createApi("").post<unknown>(deviceSessionPath(id, "end/"), {}));

const ADDRESS_NOT_ALLOWED_TEXT =
  "We can't open this device from our side: its saved address isn't one we're allowed to reach for this site (for example, a private address that isn't on one of the site's WireGuard tunnels or Site-to-Site VPNs). Check the device's address, or connect the site's tunnel.";

const GENERIC_REFUSAL_TEXT = "We couldn't open the device. Try again later.";

/**
 * A refusal, in words, with when to try again where admin says. Codes are
 * actuate_admin's (`inframap/camera/connectivity/device_session.py`); where
 * admin's detail is more specific than ours (which limit, how many hours),
 * its words win.
 */
export const refusalText = (
  refusal: { code: string; detail: string | null; retryAt: string | null },
  now: number = Date.now()
): string => {
  const { code, detail, retryAt } = refusal;
  const at = retryAt ? formatClock(retryAt, new Date(now)) : null;
  const later = (fallback: string) =>
    at ? `You can open another at ${at}.` : fallback;
  switch (code) {
    case "session_active":
      return "Someone is already using this device in a remote session. Only one session per device is allowed; try again when they're done.";
    case "busy":
    case "cluster_busy":
      return `All remote sessions are in use right now. ${
        at ? `Try again at ${at}.` : "Try again in a few minutes."
      }`;
    case "user_limit":
      return (
        detail ??
        `You have too many remote sessions open, or have opened many recently. ${later(
          "End one, or try again later."
        )}`
      );
    case "site_limit":
      return (
        detail ??
        `This site has too many remote sessions open, or its devices have been opened many times recently. ${later(
          "Try again later."
        )}`
      );
    case "group_limit":
      return `Your account has reached today's limit for remote sessions. ${later(
        "Try again tomorrow."
      )}`;
    case "user_rate_limited":
      return `You've opened many remote sessions recently. ${later(
        "Try again later."
      )}`;
    case "site_rate_limited":
      return `This site's devices have been opened many times recently. ${later(
        "Try again later."
      )}`;
    case "group_rate_limited":
      return `Your organization has reached today's limit for remote sessions. ${later(
        "Try again tomorrow."
      )}`;
    case "rate_limited":
      return `Too many remote sessions have been opened recently. ${later(
        "Try again later."
      )}`;
    case "address_not_allowed":
    case "target_not_allowed":
      return detail ?? ADDRESS_NOT_ALLOWED_TEXT;
    case "port_not_allowed":
      return detail ?? "That port can't be opened in a remote session.";
    case "address_recently_changed":
      return (
        detail ??
        "This device's address or ports changed recently. For your security it can be opened remotely once they've been unchanged for a while, or ask Support to open it."
      );
    case "address_in_use_elsewhere":
      return "This public address is also saved outside your account, so it can't be opened remotely. Ask Support to open it.";
    case "no_web_port":
      return "This device has no web port we can open. Check the ports saved for it.";
    case "no_stream_port":
      return "This device has no video stream port we can open. Check the ports saved for it.";
    case "target_not_found":
      return "We couldn't find this device's saved address. Check it's saved on the site or camera, then try again.";
    case "not_permitted":
    case "target_not_yours":
      return NOT_PERMITTED_TEXT;
    case "not_supported":
      return "Remote sessions aren't available for this site.";
    case "not_enabled_for_site":
      return "Opening devices remotely isn't turned on for this site yet.";
    case "unavailable":
      return "Opening devices remotely isn't available right now. Try again later.";
    case "start_failed":
      return "The remote session couldn't start. Try again in a few minutes.";
    case "session_not_active":
      return "This remote session isn't running any more. Open the device again.";
    case "agent_unreachable":
      return "The remote session didn't answer. Try again, or end it and open the device again.";
    case "unknown_action":
    case "invalid_path":
      return detail ?? "That action isn't available in this session.";
    default:
      return detail ?? GENERIC_REFUSAL_TEXT;
  }
};

const UNREACHABLE_TEXT =
  "We couldn't reach the device from the site's network. Check the device is on and its address is right, and that the site's tunnel or VPN is up.";
const OVERLAP_TEXT =
  "This device's address overlaps our own network, so it can't be opened remotely.";
const TRY_AGAIN_TEXT =
  "The remote session couldn't start on our side. Try again in a few minutes.";

/**
 * Why a session ended or failed (`reason` from GET), in words. Reasons come
 * from the session agent (aegissystems/remote-session `session.Reason*`),
 * admin's own sweeps and the deployer's refusals; anything else falls back
 * to admin's `detail` for a failure, then to generic words.
 */
export const endReasonText = (
  status: "ended" | "failed",
  reason: string | null | undefined,
  detail?: string | null
): string => {
  switch (reason) {
    // Ended.
    case "idle":
      return "The session ended after 5 minutes without any input.";
    case "deadline":
    case "expired":
      return "The session reached its time limit.";
    case "byte_cap":
    case "bytes":
      return "The session reached its data limit.";
    case "user_end":
    case "user_ended":
    case "user":
    case "ended_by_user":
      return "The session was ended.";
    case "terminated":
      return "The session was closed on our side.";
    case "display_exited":
      return "The remote desktop stopped unexpectedly. Open the device again.";
    // Failed to start.
    case "start_timeout":
      return "The remote session didn't start in time. Try again in a few minutes.";
    case "target_unreachable":
    case "unreachable":
    case "no_route":
      return UNREACHABLE_TEXT;
    case "route_not_ready":
      return "The session couldn't reach the site's tunnel. Check that the site's WireGuard tunnel is up, then try again.";
    case "target_is_cluster_ip":
    case "target_in_service_cidr":
      return OVERLAP_TEXT;
    case "busy":
      return "All remote sessions were in use. Try again in a few minutes.";
    case "unavailable":
      return "Opening devices remotely isn't available right now. Try again later.";
    case "sandbox_unavailable":
    case "startup_failed":
    case "start_failed":
    case "failed":
      return TRY_AGAIN_TEXT;
    default:
      if (status === "failed") {
        return (
          detail ?? "The remote session couldn't start. Try again in a minute."
        );
      }
      return "The session has ended.";
  }
};
